import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AddFormFieldButton } from '@/components/Buttons/AddFormFieldButton/AddFormFieldButton';

import { useDcrContext } from '../../_context/DcrContext';
import type { AddressSectionValuesIn } from '../../_schemas/AddressSection.validation';
import { FormSection } from '../../DcrModal/FormSection/FormSection';

import { addressSectionStyles as sx } from './AddressSection.styles';
import { AddressSectionCard } from './AddressSectionCard/AddressSectionCard';

type AddressSnapshot = AddressSectionValuesIn['addresses'][number];

export const AddressSection = () => {
	const { t } = useTranslation();

	const {
		entityCountryId,
		picklists: { countryOptions },
		isSimpleView
	} = useDcrContext();

	const { control, setValue } = useFormContext<AddressSectionValuesIn>();

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'addresses'
	});

	// Snapshot State: Stores the IMMUTABLE original data for each stable Row ID
	const [initialSnapshots, setInitialSnapshots] = useState<Record<
		string,
		AddressSnapshot
	> | null>(null);

	// Capture Snapshot ONCE when data is loaded
	useEffect(() => {
		// Only snapshot if we haven't yet, and fields are populated (data arrived from server)
		if (!initialSnapshots && fields.length > 0) {
			const snapshot: Record<string, AddressSnapshot> = {};

			// IMPORTANT: Deep copy fields to break reference to live form state
			// the RHF 'fields' contains the default values + 'id' when useFieldArray initializes
			const stableData = JSON.parse(JSON.stringify(fields));

			stableData.forEach((field: AddressSnapshot) => {
				if (field.vid_V) {
					snapshot[field.vid_V] = field;
				}
			});

			// eslint-disable-next-line react-hooks/set-state-in-effect
			setInitialSnapshots(snapshot);
		}

		// If fields are empty (0 items) on load, set empty snapshot to stop loading state
		if (!initialSnapshots && fields.length === 0) {
			setInitialSnapshots({});
		}
	}, [fields, initialSnapshots]);

	const defaultCountry = useMemo(
		() => countryOptions?.find(c => c.id === entityCountryId),
		[countryOptions, entityCountryId]
	);

	const handleAddAddress = () => {
		append(
			{
				// Standard Fields
				streetAddress1Cda_V: '',
				streetAddress2Cda_V: '',
				cityCda_V: '',
				stateCda_V: '',
				postalCodeCda_V: '',
				countryCda_V: defaultCountry?.name ?? '',

				// Extended Fields
				// Note(Filip): As of 27.01.2026 - Jonas reqested these to be hidden for the time being
				// cbsa_V: '',
				phoneCda_V: '',
				subStateAdminUnit_V: '',
				postalCodeFullUs_V: '',
				faxCda_V: '',

				// If list is empty, the new one is Primary
				primaryCda_V: null,
				businessCda_V: null,

				// Hidden fields
				vid_V: '',
				statusCda_V: 'Active',
				buildingIdentifier_V: null,
				dpvConfirmedIndicator_V: null,
				latitudeCda_V: null,
				longitudeCda_V: null
			},
			{ shouldFocus: false }
		);
	};

	const handleSetPrimary = (indexToMakePrimary: number) => {
		fields.forEach((_, idx) => {
			const isPrimary = idx === indexToMakePrimary;
			setValue(`addresses.${idx}.primaryCda_V`, isPrimary, {
				shouldDirty: true
			});
		});
	};

	return (
		<FormSection title={t('Addresses')} id='addresses'>
			<Box sx={sx.root}>
				{isSimpleView && (
					<Typography variant='body2' sx={sx.label}>
						{t('Addresses')}
					</Typography>
				)}

				<Box sx={sx.fieldWrapper}>
					{fields.length > 0 && (
						<Box sx={sx.cardsContainer}>
							{fields.map((field, index) => {
								const originalData = initialSnapshots?.[field.vid_V ?? ''];
								const isNewCard = !originalData;

								return (
									<AddressSectionCard
										key={field.id}
										index={index}
										onSetPrimary={() => handleSetPrimary(index)}
										onRemove={() => remove(index)}
										canBeRemoved={isNewCard}
										isExpanded={isNewCard}
										originalData={originalData}
										isNewCard={isNewCard}
										isLastItem={index === fields.length - 1}
									/>
								);
							})}
						</Box>
					)}

					<Box sx={sx.addNewCardButton}>
						<AddFormFieldButton onClick={handleAddAddress} />
					</Box>
				</Box>
			</Box>
		</FormSection>
	);
};
